'use client';
import type React from 'react';
import { useEffect } from 'react';

/**
 * A component that handles smooth scrolling to elements with IDs that match the URL hash.
 * It solves the problem of using loading.tsx for pages that take a long time to render
 * by waiting for all images in the target element's container to load before scrolling.
 * This prevents layout shifts that could occur when images load after scrolling.
 *
 * The component works in two modes:
 * 1. Default mode: Waits for images in the target element's parent container
 * 2. Explicit container mode: Waits for images in a container marked with data-scroll-container
 *
 * @example
 * // Default mode - waits for images in the parent container
 * const SomePage: React.FC = async () => {
 *   const data = await someLoadingData();
 *   return (
 *     <>
 *       <ScrollToAnchor isLoading={isLoading} />
 *       <div>
 *         <h2 id="section-1">Section 1</h2>
 *         <img src="image1.jpg" />
 *       </div>
 *     </>
 *   );
 * };
 *
 * @example
 * // Explicit container mode - waits for images in the marked container
 * const BlogPage: React.FC = async () => {
 *   const data = await someLoadingData();
 *   return (
 *     <>
 *       <ScrollToAnchor isLoading={isLoading} />
 *       <div data-scroll-container>
 *         <article id="blog-post-1">
 *           <h2>Blog Post 1</h2>
 *           <img src="blog-image1.jpg" />
 *         </article>
 *       </div>
 *     </>
 *   );
 * };
 *
 * @param isLoading - Whether the page content is still loading.
 *                             The component will start watching for images only after loading is complete.
 */
const ScrollToAnchor: React.FC<{ isLoading?: boolean }> = ({ isLoading }) => {
    useEffect(() => {
        if (!isLoading) {
            const hash = window.location.hash;
            if (!hash) return;

            let scrolled = false;

            const scrollToHash = () => {
                if (scrolled) return;
                scrolled = true;
                const target = document.querySelector(hash);
                if (target) {
                    target.scrollIntoView({ behavior: 'smooth' });
                }
            };

            // Wait only for eagerly-loaded images (skip lazy images that haven't started loading).
            // Next.js <Image> defaults to loading="lazy", so off-screen images won't load until
            // scrolled into view — waiting for them creates a deadlock.
            const waitForEagerImages = (containerImages: HTMLCollectionOf<HTMLImageElement>) => {
                const imagePromises = Array.from(containerImages)
                    .filter((img) => img.loading !== 'lazy' || img.complete)
                    .map((img) => {
                        if (img.complete) return Promise.resolve();
                        return new Promise<void>((resolve) => {
                            img.addEventListener('load', () => resolve(), { once: true });
                            img.addEventListener('error', () => resolve(), { once: true });
                        });
                    });

                if (imagePromises.length === 0) return Promise.resolve();

                // Timeout fallback in case event listeners are orphaned by React re-renders
                const timeout = new Promise<void>((resolve) => setTimeout(resolve, 5000));
                return Promise.race([Promise.all(imagePromises).then(() => {}), timeout]);
            };

            let waiting = false;

            // Set up a mutation observer to watch for when the target element appears
            const observer = new MutationObserver((_mutations, obs) => {
                if (waiting || scrolled) return;

                const targetElement = document.querySelector(hash);
                if (!targetElement) return;

                const container = targetElement.closest('[data-scroll-container]') || targetElement.parentElement;
                if (!container) return;

                const containerImages = container.getElementsByTagName('img');
                waiting = true;
                obs.disconnect();

                waitForEagerImages(containerImages)
                    .then(() => setTimeout(scrollToHash, 100))
                    .catch(() => setTimeout(scrollToHash, 100));
            });

            // Start observing the document body for changes
            observer.observe(document.body, {
                childList: true,
                subtree: true
            });

            // Cleanup function to disconnect the observer
            return () => observer.disconnect();
        }
    }, [isLoading]);

    return null;
};

export default ScrollToAnchor;
